import { useState } from "react";

import { cn } from "@/lib/utils";
import type { DetailFeatures } from "@/services/navigator/agent-detail-service";

import { DetailSection, DetailSubTitle, DetailTag } from "./detail-parts";

// 주요 기능 (설계서 slide 42 · Figma 03-01/04/05): 좌측 기능 목록 + 우측 상세(2단), 기능이 6개를 넘으면 목록만 스크롤하고 상세 높이는 고정.
// 원천은 단계마다 달라도(2차까지 주요 기능 · 1차+WF Component 행 · 1차만 차별화 포인트) 같은 틀을 써요. 구성 컴포넌트 카드는 2차까지 전용.

const LIST_MAX = 6;

function FeatureList({ data }: { data: DetailFeatures }) {
  const [active, setActive] = useState(0);
  const items = data.items;
  const item = items[Math.min(active, items.length - 1)];
  return (
    <div className="grid gap-6 lg:grid-cols-[363px_minmax(0,1fr)]">
      <ul className="flex flex-col self-start border-t border-nv-border" style={{ maxHeight: LIST_MAX * 62 + 1, overflowY: items.length > LIST_MAX ? "auto" : undefined }}>
        {items.map((f, i) => (
          <li key={`${f.name}-${i}`} className="border-b border-nv-border">
            <button
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={i === active}
              className={cn(
                "relative flex min-h-[61px] w-full items-center px-[18px] py-4 text-left text-[18px] leading-[29px]",
                i === active ? "font-medium text-nv-text-primary" : "text-nv-text-secondary hover:text-nv-text-primary",
              )}
            >
              {i === active && <span aria-hidden className="absolute inset-y-4 left-0 w-[2px] bg-nv-brand" />}
              {f.name ?? f.description}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex min-h-[168px] min-w-0 flex-col gap-3 self-start rounded-nv-card bg-nv-bg-page p-6 xl:p-10">
        {item.name && <h3 className="text-[20px] font-medium leading-[30px] text-nv-text-primary xl:text-[24px] xl:leading-9">{item.name}</h3>}
        {item.description && <p className="text-[15px] leading-[30px] text-nv-text-secondary xl:text-[17px] xl:leading-10">{item.description}</p>}
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-[6px] pt-2">
            {item.tags.map((t) => (
              <DetailTag key={t} className="bg-nv-surface">
                {t}
              </DetailTag>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ComponentCards({ data }: { data: DetailFeatures }) {
  const [active, setActive] = useState(0);
  const cards = data.components;
  const card = cards[Math.min(active, cards.length - 1)];
  return (
    <div className="flex flex-col gap-[14px]">
      <DetailSubTitle className="pb-1">구성 기술</DetailSubTitle>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((c, i) => (
          <button
            key={`${c.no}-${c.name}`}
            type="button"
            onClick={() => setActive(i)}
            aria-pressed={i === active}
            className={cn(
              "flex min-h-[88px] flex-col gap-[6px] rounded-nv-card border px-[17px] py-[14px] text-left",
              i === active ? "border-nv-brand bg-nv-brand-subtle" : "border-nv-border bg-nv-surface hover:border-nv-border-strong",
            )}
          >
            <span className="text-[12px] leading-[19px] text-nv-brand-text">{c.no}</span>
            <span className="text-[15px] leading-[21px] text-nv-text-primary">{c.name}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3 rounded-nv-card border border-nv-border bg-nv-surface px-6 pb-6 pt-5">
        <h3 className="text-[17px] leading-[27px] text-nv-text-primary">{card.name}</h3>
        {card.role && <p className="text-[15px] leading-6 text-nv-text-secondary">{card.role}</p>}
        {card.tags.length > 0 && (
          <div className="flex flex-wrap gap-[6px] pt-4">
            {card.tags.map((t) => (
              <DetailTag key={t}>{t}</DetailTag>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function FeaturesSection({ data }: { data: DetailFeatures }) {
  return (
    <DetailSection id="features" title="주요 기능">
      <div className="flex flex-col gap-10">
        {data.items.length > 0 && <FeatureList data={data} />}
        {data.components.length > 0 && <ComponentCards data={data} />}
      </div>
    </DetailSection>
  );
}
